import { useMutation, useQuery } from '@apollo/client';
import { InfoOutlined } from '@mui/icons-material';
import { Permission } from '@procureai/permissions';
import { usePermissions } from '@procureai/permissions-ui';
import { FormSchemaProvider, FormSchemaProviderProps } from '@procureai/ui-form-mui';
import {
  Widget,
  WidgetContent,
  WidgetEditActions,
  WidgetHeader,
  WidgetIcon,
  useCustomSnackbar,
  useScrollIntoView,
  useUuid,
} from '@procureai/ui-mui';
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  TsrctCompetitiveBiddingRuleMutationsupdateArgs,
  TsrctCompetitiveBiddingVariant,
  TsrctDemandStatus,
} from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { useHashActionEdit } from '~/hooks/useHashActionEdit/useHashActionEdit';
import { ModuleHash } from '~/modules/Module.types';
import { isDemandEditable } from '~/util/demand/isDemandEditable';

import { DemandDetailContent, DemandDetailContentProps } from './components/DemandDetailContent/DemandDetailContent';
import { DemandDetailForm } from './components/DemandDetailForm/DemandDetailForm';
import {
  DemandDetailFormData,
  demandDetailFormDataDefault,
  demandDetailFormDataSchema,
} from './components/DemandDetailForm/DemandDetailForm.schema';
import { DEMAND_DETAIL_MUTATION } from './DemandDetailMutation.gql';
import { DEMAND_DETAIL_QUERY } from './DemandDetailQuery.gql';
import { getAllCommodityGroupIds } from './DemandDetailWidget.util';

const EMPTY_CONTENTDATA: DemandDetailContentProps['data'] = {
  capabilityIds: [],
  responsibleUserIds: [],
  competitiveBiddingRule: {
    id: '',
    variant: TsrctCompetitiveBiddingVariant.NONE,
    deadline: null,
    isEditable: false,
    isMaskedBidding: false,
  },
  demandRequest: {
    id: '',
    sourceReferences: [],
    lineItems: [],
  },
};

export interface DemandDetailWidgetProps {
  // widget is part of a bigger form -> do not add own form and saving capabilities
  hasParentForm?: boolean;
}

export const DemandDetailWidget = ({ hasParentForm }: DemandDetailWidgetProps) => {
  const { entityId } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandDetailWidget' });
  const [widgetRef, active, hashAction] = useScrollIntoView({ hash: ModuleHash.DEMAND_DETAIL });
  const { onError, enqueueSuccessSnackbar } = useCustomSnackbar();
  const formId = useUuid();
  const { hasPermission } = usePermissions();

  const variables = {
    id: entityId,
  };

  const { data, loading, error } = useQuery(DEMAND_DETAIL_QUERY, {
    variables,
    skip: !entityId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const [unifiedUpdate, { loading: saving }] = useMutation(DEMAND_DETAIL_MUTATION, {
    onError,
  });

  const [editOpen, setEditOpen] = useState(hasParentForm);

  const demand = error ? null : (data?.tesseract?.demand ?? null);
  const contentData: DemandDetailContentProps['data'] = useMemo(() => {
    if (hasParentForm) {
      return EMPTY_CONTENTDATA;
    }
    if (!demand) {
      return null;
    }

    return demand;
  }, [demand, hasParentForm]);
  const competitiveBiddingRule = demand?.competitiveBiddingRule;
  const referenceDisabled = !!contentData?.demandRequest?.externalReference;
  const contentDataForm: DemandDetailFormData = useMemo(() => {
    const capabilities = contentData?.capabilityIds
      ? contentData.capabilityIds.filter((capability): capability is string => capability != null)
      : [];

    return {
      capabilities,
      referenceId: contentData?.demandRequest?.externalReference ?? null,
      responsible: contentData?.responsibleUserIds?.[0] ?? '',
      competitiveBidding: competitiveBiddingRule
        ? {
            deadline: competitiveBiddingRule.deadline ? new Date(competitiveBiddingRule.deadline) : null,
            maskBidding: { enabled: competitiveBiddingRule.variant === TsrctCompetitiveBiddingVariant.MASKED },
          }
        : {
            deadline: null,
            maskBidding: {
              enabled: false,
            },
          },
    };
  }, [contentData, competitiveBiddingRule]);

  const status = demand?.status;
  const canUpdateAwardingRule = useMemo(() => {
    return status && (status === TsrctDemandStatus.INITIALIZED || status === TsrctDemandStatus.OPEN);
  }, [status]);

  const canUpdateCompetitiveBiddingRule = useMemo(() => {
    return !loading && competitiveBiddingRule?.isEditable && hasPermission(Permission.DemandEditCompetitiveBidding);
  }, [loading, competitiveBiddingRule, hasPermission]);

  const handleEditToggle = useCallback(() => {
    setEditOpen((prev) => !prev);
  }, []);

  const handleEditApply: FormSchemaProviderProps<DemandDetailFormData>['onSubmit'] = async (submitData) => {
    if (submitData) {
      (async () => {
        // Prepare demand data
        const demandData = {
          responsibleUserIds: submitData.responsible ? [submitData.responsible] : [],
          capabilityIds: submitData.capabilities,
        };

        // Prepare competitive bidding data
        let competitiveBiddingData: TsrctCompetitiveBiddingRuleMutationsupdateArgs['data'];
        if (canUpdateCompetitiveBiddingRule) {
          if (submitData.competitiveBidding) {
            const variant = submitData.competitiveBidding.maskBidding.enabled
              ? TsrctCompetitiveBiddingVariant.MASKED
              : submitData.competitiveBidding.deadline
                ? TsrctCompetitiveBiddingVariant.UNMASKED
                : TsrctCompetitiveBiddingVariant.NONE;

            competitiveBiddingData = {
              variant,
              deadline: submitData.competitiveBidding.deadline ?? null,
            };
          } else {
            // Use current values as fallback
            competitiveBiddingData = {
              variant: competitiveBiddingRule?.variant || TsrctCompetitiveBiddingVariant.UNMASKED,
              deadline: competitiveBiddingRule?.deadline || null,
            };
          }
        }

        // Call unified mutation
        const { errors } = await unifiedUpdate({
          variables: {
            id: entityId,
            demandData,
            competitiveBiddingRuleData: competitiveBiddingData,
          },
        });

        if (!errors) {
          setEditOpen(false);
          enqueueSuccessSnackbar(t('saveSuccess'));
        }
      })();
    }
  };

  const editDisabled = !demand?.permissions.write || !isDemandEditable(data);

  useHashActionEdit({
    id: demand?.id,
    active,
    editDisabled,
    editOpen,
    setEditOpen,
    hashAction,
  });

  let content = null;

  if (editOpen && contentData) {
    const commodityGroupIds = getAllCommodityGroupIds(contentData);

    content = (
      <DemandDetailForm
        capabilityIds={(contentData.capabilityIds ?? []).filter(
          (capability): capability is string => capability != null,
        )}
        commodityGroupIds={commodityGroupIds}
        referenceDisabled={referenceDisabled}
        awardingRuleChangeDisabled={!canUpdateAwardingRule}
        competitiveBiddingChangeDisabled={!canUpdateCompetitiveBiddingRule}
      />
    );

    if (!hasParentForm) {
      content = (
        <FormSchemaProvider<DemandDetailFormData>
          id={formId}
          data={contentDataForm}
          schema={demandDetailFormDataSchema}
          defaultValues={demandDetailFormDataDefault}
          onSubmit={handleEditApply}
        >
          {content}
        </FormSchemaProvider>
      );
    }
  } else if (loading || contentData) {
    content = <DemandDetailContent data={contentData} />;
  }

  return (
    <Widget ref={widgetRef} active={active} hover={false} data-intercom-target="DemandDetailWidget">
      <WidgetHeader
        title={t('title')}
        icon={<WidgetIcon icon={<InfoOutlined fontSize="small" />} />}
        actionsRight={
          hasParentForm ? null : (
            <WidgetEditActions
              formId={formId}
              open={editOpen}
              saving={saving}
              disabled={editDisabled}
              onToggle={handleEditToggle}
            />
          )
        }
      />
      <WidgetContent>
        <div>{content}</div>
      </WidgetContent>
    </Widget>
  );
};
